import { useMemo } from 'react';

import { Quote } from '@client/model/quotesModel';
import { createQuoteWithROIandProjectedCoverage } from '@client/util/policyQuoteCalculations';
import { adjustQuotesForPremium } from '@client/util/quoteAdjustment';
import { formatROI } from '@shared';
import { isEqual } from 'lodash';

type FilterName = 'premium' | 'inflationProtection' | 'benefitDuration';

interface CombinedFilterType {
  type: 'combined' | 'separate';
  combined: number[];
  aob: number[];
  cob: number[];
}

interface QuoteWithROI extends Quote {
  roi?: number;
  roiValues?: any;
  originalROIValues?: {
    projectedReturnOnInvestment?: number;
    projectedCompoundAnnualGrowthRate?: number;
    projectedInternalRateOfReturn?: number;
    investmentPeriodYears?: number;
  };
}

interface UseAdjustedQuotesParams {
  selectedPremium: number;
  client: Client | undefined;
  selectedTab: ShopInsuranceTab;
  originalQuotes: Quote[] | undefined;
  selectedBenefitDuration: CombinedFilterType;
  selectedInflationProtection: CombinedFilterType;
  returnOnInvestmentCalculationType: ReturnOnInvestmentCalculationType;
  selectedFeatureBenefitsFilters?: string[];
}

interface UseAdjustedQuotesReturn {
  isAdjusted: FilterName[];
  originalTotalQuotes: number;
  adjustedQuotes: QuoteWithROI[];
}

const LIFETIME_DURATION_YEARS = 99;
const PREMIUM_ADJUSTMENT_THRESHOLD = 100;

const DEFAULT_FILTERS_VALUES = {
  benefitDuration: [1, 8],
  inflationProtection: [0, 5],
};

const DEFAULT_FILTERS_STATE = {
  benefitDuration: {
    type: 'combined' as const,
    combined: DEFAULT_FILTERS_VALUES.benefitDuration,
    aob: DEFAULT_FILTERS_VALUES.benefitDuration,
    cob: DEFAULT_FILTERS_VALUES.benefitDuration,
  },
  inflationProtection: {
    type: 'combined' as const,
    combined: DEFAULT_FILTERS_VALUES.inflationProtection,
    aob: DEFAULT_FILTERS_VALUES.inflationProtection,
    cob: DEFAULT_FILTERS_VALUES.inflationProtection,
  },
};

function inRange(value: number, min: number, max: number) {
  return value >= min && value <= max;
}

function isFilterApplied(
  filter: CombinedFilterType | null | undefined,
  defaultState: CombinedFilterType,
) {
  return !!filter && !isEqual(filter, defaultState);
}

function shouldAdjustPremium(quotes: Quote[], selectedPremium: number) {
  return quotes.some(
    quote => Math.abs(quote.premiumAmount - selectedPremium) > PREMIUM_ADJUSTMENT_THRESHOLD,
  );
}

/** Parse "3%", "5% Compound", "None" -> number (percent) */
function parsePercent(value?: string) {
  if (!value || value === 'None') return 0;

  const match = value.match(/(\d+(\.\d+)?)/);
  const parsed = match ? parseFloat(match[1]) : NaN;

  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Parse "4 Years", "2 yr", "Lifetime", "18 months", "None" -> years */
function parseDurationYears(value?: string) {
  if (!value || value === 'None') return 0;
  if (value.toLowerCase() === 'lifetime') return LIFETIME_DURATION_YEARS;

  const match = value.match(/(\d+)\s*(year|years|yr|yrs|month|months)/i);

  if (!match) return 0;

  const num = parseInt(match[1], 10);
  const unit = match[2].toLowerCase();

  return unit.includes('month') ? num / 12 : num;
}

/**
 * Some carriers set a single inflation string (e.g. "3% Compound") via `inflationBenefitOption`.
 * Others separate AOB/COB via `aobInflation`/`cobInflation`.
 * This normalizer yields aob/cob numbers where possible and the single-rate when applicable.
 */
function getInflationProtection(quote: Quote) {
  const hasSingle = !!quote.inflationBenefitOption && quote.inflationBenefitOption !== 'None';

  if (hasSingle) {
    const rate = parsePercent(quote.inflationBenefitOption);
    return { hasSingle: true, single: rate, aob: rate, cob: rate };
  }

  return {
    hasSingle: false,
    single: undefined as number | undefined,
    aob: parsePercent(quote.aobInflation),
    cob: parsePercent(quote.cobInflation),
  };
}

/**
 * Some carriers expose AOB/COB durations separately; others expose a total duration in months.
 * This normalizer returns aobYears, cobYears, and totalYears computed consistently.
 */
function getBenefitsDuration(quote: Quote) {
  const aobYears = parseDurationYears(quote.aobDuration);
  const cobYears = parseDurationYears(quote.cobDuration);

  if (aobYears || cobYears) {
    return { aobYears, cobYears, totalYears: aobYears + cobYears };
  }

  const totalYears = (quote.ltcBenefitDurationMonths ?? 0) / 12;

  return { aobYears: 0, cobYears: 0, totalYears };
}

function matchesTab(quote: Quote, tab: ShopInsuranceTab) {
  if (tab === 'joint') {
    return quote.policyType === 'Joint Life';
  }
  if (tab === 'couples') {
    return quote.policyType === 'Couples Discount';
  }
  // For single tab, accept both 'Single Life' and Lincoln 'Standard'
  return quote.policyType === 'Single Life' || quote.policyType === 'Standard';
}

function matchesInflationProtection(quote: Quote, filter: CombinedFilterType) {
  const { hasSingle, single, aob, cob } = getInflationProtection(quote);

  if (filter.type === 'separate') {
    const [minA, maxA] = filter.aob;
    const [minC, maxC] = filter.cob;

    if (hasSingle && typeof single === 'number') {
      return inRange(single, minA, maxA) && inRange(single, minC, maxC);
    }

    return inRange(aob, minA, maxA) && inRange(cob, minC, maxC);
  }

  const [min, max] = filter.combined;

  if (hasSingle && typeof single === 'number') {
    return inRange(single, min, max);
  }

  const isHybrid = aob !== cob;

  if (isHybrid) return inRange(aob, min, max) || inRange(cob, min, max);

  return inRange(aob, min, max);
}

function matchesBenefitsDuration(quote: Quote, filter: CombinedFilterType) {
  const { aobYears, cobYears, totalYears } = getBenefitsDuration(quote);

  if (filter.type === 'separate') {
    const [minA, maxA] = filter.aob;
    const [minC, maxC] = filter.cob;

    if (aobYears || cobYears) {
      return inRange(aobYears, minA, maxA) && inRange(cobYears, minC, maxC);
    }

    const minTotal = minA + minC;
    const maxTotal = maxA + maxC;

    return inRange(totalYears, minTotal, maxTotal);
  }

  const [minT, maxT] = filter.combined;

  if (aobYears || cobYears) {
    return inRange(aobYears + cobYears, minT, maxT);
  }

  return inRange(totalYears, minT, maxT);
}

function matchesFeatureBenefits(quote: Quote, selectedFilters: string[]): boolean {
  if (selectedFilters.length === 0) return true;

  return selectedFilters.some(filter => {
    if (filter === 'hasGlobalBenefits') return quote.hasGlobalBenefits;
    return false;
  });
}

function buildAppliedFilters(
  inflationFilter: CombinedFilterType,
  durationFilter: CombinedFilterType,
  filtered: Quote[],
  selectedPremium: number,
): FilterName[] {
  const applied: FilterName[] = [];

  if (isFilterApplied(inflationFilter, DEFAULT_FILTERS_STATE.inflationProtection)) {
    applied.push('inflationProtection');
  }

  if (isFilterApplied(durationFilter, DEFAULT_FILTERS_STATE.benefitDuration)) {
    applied.push('benefitDuration');
  }

  if (shouldAdjustPremium(filtered, selectedPremium)) {
    applied.push('premium');
  }
  return applied;
}

function useAdjustedQuotes({
  client,
  selectedTab,
  originalQuotes,
  selectedPremium,
  selectedBenefitDuration,
  selectedInflationProtection,
  returnOnInvestmentCalculationType,
  selectedFeatureBenefitsFilters = [],
}: UseAdjustedQuotesParams): UseAdjustedQuotesReturn {
  return useMemo(() => {
    if (!originalQuotes?.length || !client) {
      return { adjustedQuotes: [], isAdjusted: [], originalTotalQuotes: 0 };
    }

    const filteredQuotes = originalQuotes.filter(
      q =>
        matchesTab(q, selectedTab) &&
        matchesInflationProtection(q, selectedInflationProtection) &&
        matchesBenefitsDuration(q, selectedBenefitDuration) &&
        matchesFeatureBenefits(q, selectedFeatureBenefitsFilters),
    );

    if (!filteredQuotes.length) {
      return { adjustedQuotes: [], isAdjusted: [], originalTotalQuotes: originalQuotes.length };
    }

    const appliedFilters = buildAppliedFilters(
      selectedInflationProtection,
      selectedBenefitDuration,
      filteredQuotes,
      selectedPremium,
    );

    const quotesToProcess = appliedFilters.length
      ? adjustQuotesForPremium(filteredQuotes, selectedPremium)
      : filteredQuotes;

    const withROI = quotesToProcess
      .map<QuoteWithROI | null>(quote => {
        try {
          const enriched = createQuoteWithROIandProjectedCoverage(
            quote,
            client,
            returnOnInvestmentCalculationType,
          );

          const formattedROI = formatROI(enriched.roiValues, returnOnInvestmentCalculationType);

          return {
            ...enriched,
            formattedROI,
            // Store original ROI values for preservation when adding as funding source
            originalROIValues: {
              projectedReturnOnInvestment: enriched.roiValues?.projectedReturnOnInvestment,
              projectedCompoundAnnualGrowthRate:
                enriched.roiValues?.projectedCompoundAnnualGrowthRate,
              projectedInternalRateOfReturn: enriched.roiValues?.projectedInternalRateOfReturn,
              investmentPeriodYears: enriched.roiValues?.investmentPeriodYears,
            },
          } as QuoteWithROI;
        } catch (error) {
          console.error('Error calculating ROI for quote:', quote.insuranceQuoteId, error);
          return null; // Filter out quotes with ROI calculation errors
        }
      })
      .filter((q): q is QuoteWithROI => q !== null);

    const adjustedQuotes = withROI
      .filter(q => (q.projectedCostCoverage ?? 0) > 0)
      .sort((a, b) => (b.roi ?? 0) - (a.roi ?? 0));

    return {
      adjustedQuotes,
      isAdjusted: appliedFilters,
      originalTotalQuotes: originalQuotes.length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    client,
    selectedTab,
    originalQuotes,
    selectedPremium,
    returnOnInvestmentCalculationType,
    selectedBenefitDuration.type,
    selectedBenefitDuration.combined,
    selectedBenefitDuration.aob,
    selectedBenefitDuration.cob,
    selectedInflationProtection.type,
    selectedInflationProtection.combined,
    selectedInflationProtection.aob,
    selectedInflationProtection.cob,
    selectedFeatureBenefitsFilters,
  ]);
}

export {
  DEFAULT_FILTERS_STATE,
  DEFAULT_FILTERS_VALUES,
  useAdjustedQuotes,
  type CombinedFilterType,
  type FilterName,
};
